import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTracking } from 'react-tracking';
import { useSnackbar } from '@/features/snackbar';

import {
  PORTFOLIO_QUERY_KEY,
  PORTFOLIOS_QUERY_KEY,
} from '@/hooks/portfolio/usePortfolio';
import type { PortfolioQueryClientData } from '@/hooks/portfolio/usePortfolio';

import { changePortfolioCurrencyQueryFn } from './changePortfolioCurrencyQueryFn';

type Args = {
  onSuccess?: () => void;
};

export function useChangePortfolioCurrencyMutation({ onSuccess }: Args) {
  const queryClient = useQueryClient();
  const { addSnackbar } = useSnackbar();
  const { trackEvent } = useTracking();

  const mutation = useMutation({
    mutationFn: changePortfolioCurrencyQueryFn,
    onSuccess: (portfolio) => {
      trackEvent({
        action: 'success',
        modifier: 'changePortfolioCurrency',
      });
      addSnackbar({
        type: 'positive',
        message: 'Currency has been changed successfully',
        lifeTime: 2000,
      });
      queryClient.setQueryData<PortfolioQueryClientData>(
        [PORTFOLIO_QUERY_KEY, portfolio.id],
        () => portfolio
      );
      onSuccess?.();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PORTFOLIOS_QUERY_KEY });
    },
    onError: () => {
      addSnackbar({
        type: 'negative',
        message: `Currency hasn't been changed due to an error. Please try again later.`,
        lifeTime: 1500,
      });
    },
  });

  return mutation;
}
